import React, { useEffect, useRef, useState } from 'react';
import '../components/Navbar.css';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { lockLenis, unlockLenis } from '../utils/lenis';

const NAV_ITEMS = [
  {
    label: 'Home',
    to: '/',
    matches: ['/', '/home', '/writing/the-young-samaritan'],
  },
  { label: 'About', to: '/about', matches: ['/about'] },
  { label: 'Writing', to: '/writing', matches: ['/writing', '/writing/the-young-samaritan/reviews'] },
  { label: 'Contact', to: '/contact', matches: ['/contact'] },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';

    if (isMenuOpen) {
      lockLenis();
    } else {
      unlockLenis();
    }

    return () => {
      document.body.style.overflow = '';
      unlockLenis();
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const magnet = toggleRef.current;

    if (!magnet) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(
      '(min-width: 992px) and (hover: hover) and (pointer: fine)'
    );

    let isAttached = false;
    const bgTarget = magnet.parentElement?.querySelector('.hamburger-nav__bg');
    const magneticTarget = magnet;

    const resetEl = (element, immediate = false) => {
      if (!element) {
        return;
      }

      gsap.killTweensOf(element);

      if (immediate) {
        gsap.set(element, { clearProps: 'x,y,rotate' });
        return;
      }

      gsap.to(element, {
        x: '0em',
        y: '0em',
        rotate: '0deg',
        ease: 'elastic.out(1, 0.3)',
        duration: 1.4,
        clearProps: 'x,y,rotate',
      });
    };

    const handlePointerEnter = () => {
      resetEl(magneticTarget, true);
      resetEl(bgTarget, true);
    };

    const handlePointerMove = (event) => {
      const bounds = magnet.getBoundingClientRect();
      const strength =
        parseFloat(magnet.getAttribute('data-magnetic-strength')) || 25;
      const offsetX =
        ((event.clientX - bounds.left) / magnet.offsetWidth - 0.5) *
        (strength / 16);
      const offsetY =
        ((event.clientY - bounds.top) / magnet.offsetHeight - 0.5) *
        (strength / 16);

      gsap.to(magneticTarget, {
        x: `${offsetX}em`,
        y: `${offsetY}em`,
        rotate: '0.001deg',
        ease: 'power4.out',
        duration: 1.25,
        overwrite: 'auto',
      });

      gsap.to(bgTarget, {
        x: `${offsetX}em`,
        y: `${offsetY}em`,
        rotate: '0.001deg',
        ease: 'power4.out',
        duration: 1.25,
        overwrite: 'auto',
      });
    };

    const handlePointerLeave = () => {
      resetEl(magneticTarget, false);
      resetEl(bgTarget, false);
    };

    const attachMagnet = () => {
      if (isAttached) {
        return;
      }

      magnet.addEventListener('mouseenter', handlePointerEnter);
      magnet.addEventListener('mousemove', handlePointerMove);
      magnet.addEventListener('mouseleave', handlePointerLeave);
      isAttached = true;
    };

    const detachMagnet = () => {
      if (!isAttached) {
        return;
      }

      magnet.removeEventListener('mouseenter', handlePointerEnter);
      magnet.removeEventListener('mousemove', handlePointerMove);
      magnet.removeEventListener('mouseleave', handlePointerLeave);
      isAttached = false;
      resetEl(magneticTarget, true);
      resetEl(bgTarget, true);
    };

    const handleMediaQuery = () => {
      if (mediaQuery.matches && !isMenuOpen) {
        attachMagnet();
        return;
      }

      detachMagnet();
    };

    handleMediaQuery();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaQuery);
    } else {
      mediaQuery.addListener(handleMediaQuery);
    }

    return () => {
      detachMagnet();

      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaQuery);
      } else {
        mediaQuery.removeListener(handleMediaQuery);
      }
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen((currentState) => !currentState);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isCurrentPath = (matches) => matches.includes(location.pathname);

  return (
    <nav
      data-navigation-status={isMenuOpen ? 'active' : 'not-active'}
      className='navigation'
      aria-label='Primary'
    >
      <button
        type='button'
        data-navigation-toggle='close'
        className='navigation__dark-bg'
        aria-label='Close navigation'
        onClick={closeMenu}
      ></button>
      <div className='hamburger-nav'>
        <div className='hamburger-nav__bg'></div>
        <div className='hamburger-nav__group'>
          <p className='hamburger-nav__menu-p'>Explore</p>
          <ul className='hamburger-nav__ul'>
            {NAV_ITEMS.map((item) => (
              <li key={item.to} className='hamburger-nav__li'>
                <Link
                  to={item.to}
                  className='hamburger-nav__a'
                  aria-current={isCurrentPath(item.matches) ? 'page' : undefined}
                  onClick={closeMenu}
                >
                  <p className='hamburger-nav__p'>{item.label}</p>
                  <div className='hamburger-nav__dot'></div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <button
          ref={toggleRef}
          type='button'
          data-navigation-toggle='toggle'
          className='hamburger-nav__toggle'
          data-magnetic-strength='42'
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          onClick={toggleMenu}
        >
          <span
            className='hamburger-nav__toggle-inner'
          >
            <span
              className='hamburger-nav__toggle-content'
            >
              <span className='hamburger-nav__toggle-bar'></span>
              <span className='hamburger-nav__toggle-bar'></span>
            </span>
          </span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
